import {
  addDays,
  addMonths,
  endOfDay,
  endOfMonth,
  format,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { ServiceCategory } from '@prisma/client';
import { AvailableTimes, DayItem } from '../../interfaces';
import { getAvailableDays } from '../../services/availability';
import useUser from '../global/useUser';
import { TakenSessionSlot } from '../../types/appointments';

interface Params {
  employeeId: string;
  duration: string;
  rescheduleAppointmentId?: string;
  serviceCategory?: ServiceCategory;
  takenSlots?: TakenSessionSlot[];
  customerView?: boolean;
}

function useEmployeeAvailableDays({
  duration,
  employeeId,
  rescheduleAppointmentId,
  serviceCategory,
  takenSlots,
  customerView,
}: Params) {
  const { user: loadedUser } = useUser();
  const [timezone] = React.useState(loadedUser?.timezone || 'US/Pacific');

  const [initialCurrentMonth, setInitialCurrentMonth] = React.useState<Date>(
    new Date(),
  );
  const [selectedMonth, setSelectedMonth] = React.useState<Date | null>(null);

  const queryKey = React.useMemo(
    () => [
      'monthAvailability',
      selectedMonth,
      employeeId,
      duration,
      timezone,
      loadedUser?.business.id,
      rescheduleAppointmentId,
      serviceCategory,
      takenSlots,
      customerView,
    ],
    [
      selectedMonth,
      employeeId,
      duration,
      timezone,
      loadedUser?.business.id,
      rescheduleAppointmentId,
      serviceCategory,
      takenSlots,
      customerView,
    ],
  );

  const fetchAvailableDays = React.useCallback(
    // Explicit return type — the function recurses, so it cannot be inferred.
    async (monthsToAdd = 0): Promise<DayItem[] | null> => {
      if (!employeeId || !duration) return [];
      const isInitialLoad = !selectedMonth;

      const actualMonth = selectedMonth ?? addMonths(new Date(), monthsToAdd);

      // Set start and end dates to display on the calendar
      const firstOfMonth = startOfDay(startOfMonth(actualMonth));
      const lastOfMonth = endOfDay(endOfMonth(actualMonth));

      // Set available days based on onsched API call
      const response = await getAvailableDays({
        startDate: format(firstOfMonth, 'yyyy-MM-dd'),
        endDate: format(lastOfMonth, 'yyyy-MM-dd'),
        duration: parseInt(duration, 10),
        employeeId,
        timezone,
        businessId: loadedUser?.business.id,
        rescheduleAppointmentId,
        serviceCategory,
        takenSlots,
        customerView,
      });
      if (!response) throw new Error('Cannot get available days');

      const tempAvailableDays: DayItem[] = [];
      let loopDate = firstOfMonth;
      while (loopDate <= lastOfMonth) {
        const loopDateString = format(loopDate, 'yyyy-MM-dd');

        if (
          response.availableTimes.some(
            (availableTime: AvailableTimes) =>
              availableTime.date === loopDateString,
          )
        ) {
          tempAvailableDays.push({
            date: loopDateString,
            available: true,
          });
        } else {
          tempAvailableDays.push({
            date: loopDateString,
            available: false,
          });
        }

        loopDate = addDays(loopDate, 1);
      }

      if (tempAvailableDays.some((day: DayItem) => day.available)) {
        return tempAvailableDays;
      }

      if (isInitialLoad) {
        const isTwelveMonthsAway = monthsToAdd >= 12;
        // If no available days in the current month, try the next month
        if (!isTwelveMonthsAway) {
          setInitialCurrentMonth(addMonths(actualMonth, 1));
          return fetchAvailableDays(monthsToAdd + 1);
        }
        // null marks "no availability within 12 months" without failing the query
        return null;
      }
      return [];
    },
    [
      employeeId,
      duration,
      selectedMonth,
      timezone,
      loadedUser?.business.id,
      rescheduleAppointmentId,
      serviceCategory,
      takenSlots,
      customerView,
      setInitialCurrentMonth,
    ],
  );

  const {
    data: rawData,
    isLoading,
    isFetching,
    isPlaceholderData,
    isRefetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => fetchAvailableDays(),
    enabled: !!employeeId && !!duration,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: 1000 * 30, // 30 seconds
    gcTime: 1000 * 60, // 60 seconds
  });

  const data = rawData ?? [];
  const noAvailableDays =
    rawData === null && !selectedMonth && !isPlaceholderData;

  return {
    state: {
      selectedMonth,
      setSelectedMonth,
      initialCurrentMonth,
      setInitialCurrentMonth,
    },
    query: {
      data,
      isFetching,
      isLoading,
      isError,
      error,
      isRefetching,
      refetch,
    },
    noAvailableDays,
  };
}

export default useEmployeeAvailableDays;
